/**
 * Parses `href` the way a browser does before deciding whether it is safe to link to.
 *
 * `URL` strips tabs, newlines and other characters a browser ignores inside a URL, and resolves
 * a scheme-less value against `base`, before reporting a protocol — a check on the raw string
 * instead (e.g. a `^[a-z]+:` regex) can be bypassed by hiding the scheme behind one of those
 * characters (`java<TAB>script:`, `java<LF>script:`, a leading control character). A relative
 * address has no protocol of its own, so it inherits the dummy base's `https:` and is treated as
 * safe whenever `https:` is in `schemes`.
 */
export function hasScheme(href: string, schemes: Set<string>): boolean {
  try {
    return schemes.has(new URL(href, 'https://relative.invalid').protocol);
  } catch {
    return false;
  }
}

const SAFE_LINK_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/** The one rule for a link a resident-facing page may render live: a relative address, or http/https/mailto/tel. */
export const isSafeHref = (href: string): boolean => hasScheme(href, SAFE_LINK_SCHEMES);

const EXPLICIT_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Whether `value` starts with its own URI scheme (`https:`, `mailto:`, `javascript:`, …), as
 * opposed to a bare host or path that only becomes a scheme once resolved against a base — unlike
 * `hasScheme`, this does not resolve `value` against a dummy base first, so a bare host such as
 * `example.org` reports `false` rather than inheriting the base's `https:`.
 */
export const hasExplicitScheme = (value: string): boolean => EXPLICIT_SCHEME.test(value);
